// checkout/utils/submitForm.ts

import { checkoutData } from '../init';
import { populateForm } from './populateForm';
import { updateFormStatusClass } from './formStatus';
import { renderPaymentOptions } from '../steps/paymentMethodRenderer';
import { decodeHtml } from '../../utils/decodeHtml';
import { NotificationService, NotificationType } from '../../utils/NotificationService';
import { executeStepFocusCallbacks } from './stepFocusCallbacks';
import { initOrderSummary } from './handleOrderSummary';
import { resetAccountDetailsForm } from '../steps/accountDetailsReset';
import { dispatchAuthenticationFailed } from '../../events/authenticationEvent';

/**
 * Clears all validation errors from a form.
 *
 * - Hides all `.error-message` elements and restores `aria-hidden="true"`.
 * - Removes `aria-invalid` from every `.form__input`.
 * - Cleans `aria-describedby` by removing any referenced error IDs,
 *   but preserves non-error description IDs (e.g., hints).
 *
 * @param {HTMLFormElement} form - The form whose validation state should be reset.
 * @returns {void}
 */
export function clearFormErrors(form: HTMLFormElement): void {
	form.querySelectorAll<HTMLElement>('.error-message').forEach((errorElement) => {
		errorElement.hidden = true;
		errorElement.setAttribute('aria-hidden', 'true');
	});

	// Reset aria-invalid and strip any error IDs from aria-describedby
	form.querySelectorAll<HTMLElement>('.form__input').forEach((field) => {
		field.removeAttribute('aria-invalid');

		const describedBy = field.getAttribute('aria-describedby');
		if (!describedBy) return;

		const ids = describedBy.split(/\s+/).filter((id) => {
			const el = document.getElementById(id);
			return !(el && el.classList.contains('error-message'));
		});

		if (ids.length) {
			field.setAttribute('aria-describedby', ids.join(' '));
		} else {
			field.removeAttribute('aria-describedby');
		}
	});
}

type FieldError = { error?: boolean; code?: string; message?: string } | boolean;

/**
 * Displays server-side validation errors on a form.
 *
 * For each field error:
 * - Shows the corresponding `.error-message` element.
 * - Updates its text and `aria-hidden` state.
 * - Sets `aria-invalid="true"` on the input.
 * - Adds the error element’s ID to the input’s `aria-describedby`
 *   (preserving any existing non-error descriptions).
 *
 * Expects error message elements to follow the pattern:
 *   `${form.id}-${fieldName}-error`
 *
 * @param {HTMLFormElement} form - The form containing the inputs.
 * @param {Record<string, FieldError>} errors - Field error map returned by the server:
 *   - boolean → simple invalid flag
 *   - object  → `{ error, code?, message? }`
 * @returns {void}
 */
export function displayFormErrors(form: HTMLFormElement, errors: Record<string, FieldError>): void {
	const formId = form.id === 'registrationForm' ? 'registerForm' : form.id;
	const stepId = formId.replace(/Form$/, '');

	Object.entries(errors).forEach(([fieldName, fieldError]) => {
		const hasError = typeof fieldError === 'boolean' ? fieldError : Boolean(fieldError?.error);
		const message =
			typeof fieldError === 'object' && fieldError?.message ? String(fieldError.message) : '';

		// normalise field name
		let normalised = fieldName;
		const stepPrefix = `${stepId}.`;
		const addressPrefix = `${stepId}.address.`;
		if (normalised.startsWith(addressPrefix))
			normalised = normalised.slice(addressPrefix.length);
		else if (normalised.startsWith(stepPrefix))
			normalised = normalised.slice(stepPrefix.length);

		const idFragment = normalised.replace(/\./g, '-');
		const errorId = `${formId}-${idFragment}-error`;
		const inputId = `${formId}-${idFragment}`;

		// toggle error message element
		const errorEl = document.getElementById(errorId) as HTMLElement | null;
		if (errorEl) {
			errorEl.hidden = !hasError;
			if (hasError) {
				(errorEl as HTMLElement).removeAttribute('aria-hidden');
				if (message) (errorEl as HTMLElement).textContent = decodeHtml(message);
			} else {
				(errorEl as HTMLElement).setAttribute('aria-hidden', 'true');
			}
			if (typeof fieldError === 'object' && fieldError?.code) {
				(errorEl as HTMLElement).dataset.code = String(fieldError.code);
			} else {
				delete (errorEl as any)?.dataset?.code;
			}
		} else {
			console.warn(`Missing error element: #${errorId}`);
		}

		// toggle aria-invalid and aria-describedby on input
		let field = form.querySelector<HTMLElement>(`#${inputId}`);
		if (!field) {
			// fallback: convert lastName → last-name
			const kebabFragment = idFragment.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

			const kebabInputId = `${formId}-${kebabFragment}`;
			field = form.querySelector<HTMLElement>(`#${kebabInputId}`);
		}
		if (!field) {
			// final fallback: match by name attribute
			const nameCandidates = new Set<string>([
				fieldName,
				normalised,
				`${stepId}.${normalised}`,
				`${stepId}.address.${normalised}`,
			]);

			for (const name of nameCandidates) {
				const byName = form.querySelector<HTMLElement>(`[name="${name}"]`);
				if (byName) {
					field = byName;
					break;
				}
			}
		}
		if (field) {
			// aria-invalid
			if (hasError) field.setAttribute('aria-invalid', 'true');
			else field.removeAttribute('aria-invalid');

			// aria-describedby (append/remove error id)
			const existing = field.getAttribute('aria-describedby');
			let ids = existing ? existing.split(/\s+/).filter(Boolean) : [];

			const hasId = ids.includes(errorId);

			if (hasError && !hasId) {
				ids.push(errorId);
			} else if (!hasError && hasId) {
				ids = ids.filter((id) => id !== errorId);
			}

			if (ids.length) {
				field.setAttribute('aria-describedby', ids.join(' '));
			} else {
				field.removeAttribute('aria-describedby');
			}
		}
	});

	// Clamp auth visibility so only one panel is shown after errors render
	if (form.id === 'loginForm' || form.id === 'registrationForm') {
		const loginSelected =
			document.getElementById('tab-login')?.getAttribute('aria-selected') === 'true';
		const login = document.getElementById('loginForm') as HTMLFormElement | null;
		const register = document.getElementById('registrationForm') as HTMLFormElement | null;
		if (login && register) {
			login.toggleAttribute('hidden', !loginSelected);
			register.toggleAttribute('hidden', loginSelected);
		}
	}
}

/**
 * Enables user interaction with all input fields within a given form that match the `.form__input` selector.
 *
 * This function removes the `pointer-events: none` property from each matching `<input>` element,
 * allowing user interaction with those fields again.
 *
 * @param {HTMLFormElement} form - The form element containing the input fields to enable.
 * @returns {void}
 */
export function enableFormFields(form: HTMLFormElement): void {
	const fields = form.querySelectorAll<HTMLInputElement>('.form__input');
	fields?.forEach((field) => {
		field.style.removeProperty('pointer-events');
	});
}

/**
 * Disables user interaction with all input fields within a given form that match the `.form__input` selector.
 *
 * This function applies a `pointer-events: none` property to each matching `<input>` element,
 * preventing user interaction with those fields.
 *
 * @param {HTMLFormElement} form - The form element containing the input fields to disable.
 * @returns {void}
 */
export function disableFormFields(form: HTMLFormElement): void {
	const fields = form.querySelectorAll<HTMLInputElement>('.form__input');
	fields?.forEach((field) => {
		field.style.cssText = 'pointer-events: none';
	});
}

/**
 * Pushes the authenticated customer's number to the GTM dataLayer if it
 * has not already been added. Used to make the customer number available
 * for tracking on subsequent checkout steps.
 *
 * @param {string} customerNumber - The customer number value obtained from the response.
 * @returns {void}
 */
export function updateDataLayerCustomerNumber(customerNumber: string): void {
	if (!customerNumber) return;

	window.dataLayer = window.dataLayer || [];

	const checkCustomerNumberEntry = window.dataLayer?.some(
		(item: any) => item.customer_number === customerNumber,
	);

	if (!checkCustomerNumberEntry) {
		window.dataLayer.push({
			event: 'customer_authenticated',
			customer_number: customerNumber,
			auth_context: 'checkout_login',
		});
	}
}

/**
 * Submits a form using fetch, validates with browser validation,
 * and updates form state and content dynamically.
 *
 * @param {HTMLFormElement} form - The form to be submitted.
 * @param {string} [overrideAction] - An optional URL to override the form's action attribute.
 * @returns {Promise<void>}
 */
export async function submitFormAndUpdateStep(
	form: HTMLFormElement,
	overrideAction?: string,
): Promise<void> {
	let submitBtn: HTMLButtonElement | null = null;

	try {
		clearFormErrors(form);

		const action = overrideAction || form.getAttribute('action');
		if (!action) {
			console.error('❌ No form action specified.');
			return;
		}

		// PCA input cleanup if applicable
		const searchInput = form.querySelector<HTMLInputElement>('#address-search');
		if (searchInput && searchInput.name) {
			searchInput.name = '';
		}

		// Animate the submit button to imply the form is loading
		submitBtn = form.querySelector('.form__submit') as HTMLButtonElement | null;
		if (submitBtn) {
			submitBtn.classList.add('btn-loading');
			submitBtn.inert = true;
		}

		// Disable the form fields to avoid user tampering during submission
		disableFormFields(form);

		const formData = new FormData(form);

		const response = await fetch(action, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
				'X-Requested-With': 'XMLHttpRequest',
			},
			body: new URLSearchParams(formData as any),
		});

		const status = response.status;
		const text = await response.text();
		const doc = new DOMParser().parseFromString(text, 'text/html');
		const scripts = doc.querySelectorAll<HTMLScriptElement>('script[id^="step-json-"]');

		// Handle redirect-to-final-step case (e.g. response is just a string URL)
		if (status >= 300 && status < 400 && !text.trim().startsWith('<')) {
			const target = text.trim();
			const base = window.serverData?.contextPath || '';

			const customerNumber = response.headers.get('x-customer-number');
			if (customerNumber) updateDataLayerCustomerNumber(customerNumber.toString());

			window.location.href = base + '/' + target;
			return;
		}

		// Only continue if response is 200 or 400 and contains HTML. This has to be checked before we
		// look for the step JSON: an error response carries no step blocks, so testing for those first
		// would swallow the failure and leave the customer stuck on a form that never recovers.
		if (![200, 400].includes(status)) {
			console.error(`❌ Request failed with status ${status}`);
			window.location.href = `${window.serverData.contextPath}/checkout/start`;
			return;
		}

		if (!scripts.length) {
			console.warn('⚠️ No <script id="step-json-..."> blocks found.');
			return;
		}

		// Scroll back to top / <main> tag
		const main = document.querySelector('#main__content') as HTMLElement;
		if (main) main.scrollIntoView({ behavior: 'smooth' });

		scripts.forEach((script) => {
			const stepId = script.id.replace('step-json-', '');

			try {
				const json = JSON.parse(script.textContent || '{}');
				checkoutData[stepId] = json;

				// If this step is now FOCUSED, run and clear its queued focus callbacks.
				executeStepFocusCallbacks(stepId, json?.status);

				if (json?.fields) {
					const dynamicOptions: Record<string, any[]> = {};
					const customerNumber = checkoutData['login']?.fields.customerNumber.toString();

					updateDataLayerCustomerNumber(customerNumber);

					if (json.titles) {
						const key =
							stepId === 'accountDetails' ? 'title' : 'deliveryAddress.address.title';
						dynamicOptions[key] = json.titles;
					}

					if (json.countries) {
						const isDigitalOnly = json.isDigitalOnly === 'true';
						dynamicOptions['deliveryAddress.address.countryCode'] = json.countries
							.filter((c: any) => isDigitalOnly || c.isAllowedForDelivery === 'true')
							.map((c: any) => ({ value: c.code, text: c.name }));
					}

					if (json.deliveryInstructions && Array.isArray(json.deliveryInstructions)) {
						dynamicOptions['deliveryAddress.deliveryInstructionId'] =
							json.deliveryInstructions.map((inst: any) => ({
								value: String(inst.id),
								text: String(inst.description),
							}));
					}

					populateForm(`${stepId}Form`, json.fields, dynamicOptions);

					if (stepId === 'paymentMethod') {
						renderPaymentOptions(json);
					}

					// Remove submit button focus
					if (submitBtn) submitBtn.blur();
				}

				updateFormStatusClass(`${stepId}Form`, stepId);

				if (json.errors) {
					if (stepId === 'accountDetails') {
						// Clear all hidden fields on the account details form after an error
						resetAccountDetailsForm(form);
					} else if (stepId === 'login' && form.id === 'loginForm') {
						dispatchAuthenticationFailed('login');
					} else if (stepId === 'register' && form.id === 'registrationForm') {
						dispatchAuthenticationFailed('register');
					}

					// Reveal manual address fields on address errors
					if (
						Object.keys(json.errors).some((key) =>
							key.startsWith('deliveryAddress.address.'),
						)
					) {
						const manualFields = form.querySelector('.manual-address-fields');
						if (manualFields) manualFields.classList.remove('hidden');

						const entryWrapper = form.querySelector('.manual-address-entry-wrapper');
						if (entryWrapper) entryWrapper.classList.add('hidden');
					}

					displayFormErrors(form, json.errors);

					// If the error "error.already.registered" is present,
					// move the error to login, switch to Login tab/panel, and focus login.
					if (
						Object.values(json.errors || {}).some(
							(err: any) => err?.code === 'error.already.registered',
						)
					) {
						const regErr = document.querySelector(
							'#registerForm-email-error',
						) as HTMLElement | null;
						const loginErr = document.querySelector(
							'#loginForm-email-error',
						) as HTMLElement | null;

						if (regErr && loginErr) {
							// copy message across; do NOT destroy the register error element/content
							const copy = (regErr.textContent || '').trim();
							if (copy) loginErr.textContent = copy;
							loginErr.removeAttribute('hidden');
							loginErr.removeAttribute('aria-hidden');

							// hide register error instead of clearing/removing it
							regErr.setAttribute('hidden', 'true');
							regErr.setAttribute('aria-hidden', 'true');
						}

						// Switch tabs/panels for the button+tabpanel setup
						const tabLogin = document.getElementById(
							'tab-login',
						) as HTMLButtonElement | null;
						const tabRegister = document.getElementById(
							'tab-register',
						) as HTMLButtonElement | null;
						const loginPanel = document.getElementById(
							'loginForm',
						) as HTMLFormElement | null;
						const registerPanel = document.getElementById(
							'registrationForm',
						) as HTMLFormElement | null;

						if (tabLogin && tabRegister) {
							tabLogin.setAttribute('aria-selected', 'true');
							tabRegister.setAttribute('aria-selected', 'false');
						}
						if (loginPanel && registerPanel) {
							loginPanel.removeAttribute('hidden');
							registerPanel.setAttribute('hidden', '');
						}

						// Focus login input (email preferred)
						const focusTarget =
							(document.getElementById('loginForm-email') as HTMLElement | null) ||
							(document.getElementById('loginForm-password') as HTMLElement | null) ||
							(loginPanel?.querySelector<HTMLElement>(
								'input, select, textarea, button',
							) ??
								null);
						if (focusTarget) focusTarget.focus({ preventScroll: false });
					}

					// Clear password field if the login fails.
					if (
						Object.values(json.errors || {}).some(
							(err: any) => err?.code === 'error.login.failed',
						)
					) {
						const passwordInput = document.getElementById(
							'loginForm-password',
						) as HTMLInputElement;
						if (passwordInput) passwordInput.value = '';
					}
				}

				const msgs = Object.values(json?.messages || {});
				msgs.forEach((msg: any) => {
					const existingNotification =
						document.querySelector<HTMLElement>('wc-notification');
					if (existingNotification) existingNotification.remove();
					NotificationService.showNotification(
						NotificationType[msg.type as keyof typeof NotificationType] ||
							NotificationType.info,
						msg.type || '',
						msg.messageText || '',
					);
				});
			} catch (err) {
				console.error(`❌ Failed to parse JSON for '${stepId}':`, err);
			}
		});

		// Update the order summary
		const orderSummary = document.getElementById('order-summary');
		const newOrderSummary = doc.getElementById('order-summary');

		if (orderSummary && newOrderSummary) {
			orderSummary.innerHTML = newOrderSummary.innerHTML;
			initOrderSummary();
		}
	} catch (err) {
		console.error('❌ Form submission error:', err);
	} finally {
		// Whatever came back, the form has to end up usable again. Clearing this only on the success
		// path is what left customers looking at a spinner over fields they could no longer type into.
		if (submitBtn) {
			submitBtn.classList.remove('btn-loading');
			submitBtn.inert = false;
		}
		enableFormFields(form);
	}
}
